import { useMemo, useState } from "react";
import { ConnectProto } from "@racoon/api";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import {
  type FilterFilterParam,
  type SearchableSelectOption,
  parseIdList,
  useUrlFilters,
} from "@racoon/ui";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import {
  useListCounterpartiesQuery,
  useListServiceReceiversQuery,
} from "~/shared/api/organization/organization.grpc";
import { useListCustomerTypesQuery } from "~/shared/api/organization-labels/organization-labels.grpc";
import { useListPickupLocationsQuery } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { useListContainerTypesQuery } from "~/shared/api/container-type/container-type.grpc";
import { useListUsersQuery } from "~/shared/api/user/user.grpc";
import { useListTrucksQuery } from "~/shared/api/truck/truck.grpc";
import { JOB_TYPE_CATEGORY_ORDER } from "~/shared/lib/translationMaps";
import { personName } from "./job-report";

/** The report filters, kept in the URL like every other list page's. */
export type ReportFilterKey =
  | "serviceReceiver"
  | "pickupLocation"
  | "containerType"
  | "driver"
  | "truck"
  | "jobStatus"
  | "jobType"
  | "jobCategory"
  | "counterpartyView"
  | "counterparties"
  | "customerTypes"
  | "jobTypes"
  | "containerTypes";

const PARAM: Record<ReportFilterKey, string> = {
  serviceReceiver: "service_receiver_id",
  pickupLocation: "pickup_location_id",
  containerType: "container_type_id",
  driver: "driver_id",
  truck: "truck_id",
  jobStatus: "job_status",
  jobType: "job_type",
  jobCategory: "job_category",
  counterpartyView: "counterparty_view",
  counterparties: "counterparty_ids",
  customerTypes: "customer_type_ids",
  jobTypes: "job_types",
  containerTypes: "container_type_ids",
};

const KEYS = Object.keys(PARAM) as ReportFilterKey[];

/** The statuses «Невывоз» lists; its job status filter picks one. */
export const NON_COLLECTION_STATUSES = [
  ConnectProto.JobStatus.NOT_COMPLETED,
  ConnectProto.JobStatus.CANCELLED,
];

/** Each filter's value; "" means unfiltered. A multiselect's is its picked ids joined by commas. */
export type ReportFilterValues = Record<ReportFilterKey, string>;

/** The args the report and its filter options both send, so each pair shares one cache entry. */
export const activeCounterpartiesArgs = (orgId: bigint) => ({
  organizationId: orgId,
  status: ConnectProto.ContractStatus.ACTIVE,
});
export const customerTypesArgs = (orgId: bigint) => ({ ownerOrganizationIds: [orgId] });

type Option = { id: string; name: string };

const byName = (a: Option, b: Option) => a.name.localeCompare(b.name);

const options = <T>(items: T[], id: (item: T) => bigint, name: (item: T) => string): Option[] =>
  items
    .map((item) => ({ id: String(id(item)), name: name(item) || String(id(item)) }))
    .sort(byName);

export function useReportFilterValues(): ReportFilterValues {
  const { sp } = useUrlFilters();
  return useMemo(
    () =>
      Object.fromEntries(KEYS.map((key) => [key, sp.get(PARAM[key]) ?? ""])) as ReportFilterValues,
    [sp],
  );
}

/**
 * The filter-sheet params for a report's filters and their summary line for the exports.
 * Options load when the sheet opens or the filter is set, so the report doesn't wait on them.
 */
export function useReportFilterParams(keys: readonly ReportFilterKey[]) {
  const t = useT();
  const { activeCompanyId } = useAuthSession();
  const { patch } = useUrlFilters();
  const values = useReportFilterValues();
  const orgId = BigInt(activeCompanyId ?? 0);
  const [sheetOpened, setSheetOpened] = useState(false);
  const skip = (key: ReportFilterKey) => ({
    skip: !activeCompanyId || !keys.includes(key) || (!sheetOpened && !values[key]),
  });

  // Directory lists for the filter options, fetched only for the filters a report offers.
  const serviceReceivers = useListServiceReceiversQuery(
    { organizationId: orgId },
    skip("serviceReceiver"),
  );
  // The same list the schedule reports load, so the two share one request.
  const pickupLocations = useListPickupLocationsQuery(
    { serviceProviderIds: [orgId] },
    skip("pickupLocation"),
  );
  const containerTypes = useListContainerTypesQuery(
    { ownerOrganizationIds: [orgId] },
    skip("containerType"),
  );
  const drivers = useListUsersQuery(
    { ownerOrganizationIds: [orgId], roles: [ConnectProto.UserRole.DRIVER] },
    skip("driver"),
  );
  const trucks = useListTrucksQuery({ ownerOrganizationIds: [orgId] }, skip("truck"));
  // The report loads these same lists, so each pair shares one request.
  const counterparties = useListCounterpartiesQuery(
    activeCounterpartiesArgs(orgId),
    skip("counterparties"),
  );
  const customerTypes = useListCustomerTypesQuery(customerTypesArgs(orgId), skip("customerTypes"));
  const containerTypeList = useListContainerTypesQuery(
    { ownerOrganizationIds: [orgId] },
    skip("containerTypes"),
  );

  const filterParams = useMemo<FilterFilterParam[]>(() => {
    const enumOption = (id: string) => ({ id, name: t(Namespace.Enum, id as TranslationKey) });
    // Every job type, the ones the organization turned off too: a contract can still price a
    // retired type, and the filter has to find it.
    const jobTypeOptions = Object.keys(ConnectProto.JobType)
      .filter((name) => Number.isNaN(Number(name)) && name !== "UNDEFINED")
      .map((name) => enumOption(`JOB_TYPE_${name}`))
      .sort(byName);
    const catalog: Record<
      ReportFilterKey,
      { label: string; options: SearchableSelectOption[]; searchable?: boolean; multiple?: boolean }
    > = {
      serviceReceiver: {
        label: t(Namespace.Analytics, "table.customer"),
        options: options(
          serviceReceivers.data?.organizations ?? [],
          (o) => o.id,
          (o) => o.name,
        ),
        searchable: true,
      },
      pickupLocation: {
        label: t(Namespace.Analytics, "columns.site"),
        options: options(
          pickupLocations.data?.pickupLocations ?? [],
          (l) => l.id,
          (l) => l.name,
        ),
        searchable: true,
      },
      containerType: {
        label: t(Namespace.Analytics, "columns.containerType"),
        options: options(
          containerTypes.data?.containerTypes ?? [],
          (c) => c.id,
          (c) => c.name,
        ),
        searchable: true,
      },
      driver: {
        label: t(Namespace.Analytics, "collection.driver"),
        options: options(
          drivers.data?.users ?? [],
          (u) => u.id,
          (u) => personName(u),
        ),
        searchable: true,
      },
      truck: {
        label: t(Namespace.Analytics, "collection.truck"),
        options: options(
          trucks.data?.trucks ?? [],
          (tr) => tr.id,
          (tr) => tr.plateNumber,
        ),
        searchable: true,
      },
      jobStatus: {
        label: t(Namespace.Analytics, "nonCollection.action"),
        options: NON_COLLECTION_STATUSES.map((status) => {
          const id = ConnectProto.JobStatus[status];
          return { id, name: t(Namespace.Enum, `JOB_STATUS_${id}` as TranslationKey) };
        }),
      },
      jobType: {
        label: t(Namespace.Analytics, "schedules.scheduleType"),
        options: jobTypeOptions,
        searchable: true,
      },
      jobCategory: {
        label: t(Namespace.Analytics, "schedules.wasteType"),
        options: JOB_TYPE_CATEGORY_ORDER.map(enumOption),
      },
      // Unset is one row per counterparty; the only option splits the rows by contract tariff.
      counterpartyView: {
        label: t(Namespace.Analytics, "counterpartyRegistry.view"),
        options: [
          { id: "contract", name: t(Namespace.Analytics, "counterpartyRegistry.viewByContract") },
        ],
      },
      counterparties: {
        label: t(Namespace.Analytics, "counterpartyRegistry.counterparties"),
        // The BIN tells look-alike names apart.
        options: (counterparties.data?.counterparties ?? [])
          .flatMap(({ organization: o }) =>
            o ? [{ id: String(o.id), name: o.name || String(o.id), secondary: o.bin }] : [],
          )
          .sort(byName),
        multiple: true,
      },
      customerTypes: {
        label: t(Namespace.Analytics, "counterpartyRegistry.customerType"),
        options: options(
          customerTypes.data?.labels ?? [],
          (l) => l.id,
          (l) => l.name,
        ),
        multiple: true,
      },
      jobTypes: {
        label: t(Namespace.Analytics, "counterpartyRegistry.jobType"),
        options: jobTypeOptions,
        multiple: true,
      },
      containerTypes: {
        label: t(Namespace.Analytics, "columns.containerType"),
        options: options(
          containerTypeList.data?.containerTypes ?? [],
          (c) => c.id,
          (c) => c.name,
        ),
        multiple: true,
      },
    };
    return keys.map((key) => {
      const { multiple, ...entry } = catalog[key];
      return {
        key: PARAM[key],
        type: multiple ? ("multiselect" as const) : ("select" as const),
        value: values[key],
        ...entry,
      };
    });
  }, [
    keys,
    values,
    t,
    serviceReceivers.data,
    pickupLocations.data,
    containerTypes.data,
    drivers.data,
    trucks.data,
    counterparties.data,
    customerTypes.data,
    containerTypeList.data,
  ]);

  const onFilterParamsChange = (key: string, value: string | null) => patch({ [key]: value });
  const onFilterSheetOpenChange = (open: boolean) => open && setSheetOpened(true);

  const filterInfo = useMemo(() => {
    const labels = filterParams.flatMap((param) => {
      if (!param.value) return [];
      const ids = param.type === "multiselect" ? parseIdList(param.value) : [param.value];
      const names = ids.map((id) => param.options?.find((o) => o.id === id)?.name ?? id);
      return [`${param.label}: ${names.join(", ")}`];
    });
    return labels.length ? labels.join(" · ") : undefined;
  }, [filterParams]);

  return { filterParams, onFilterParamsChange, onFilterSheetOpenChange, filterInfo };
}
